import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';

import {
  gray100,
  gray80,
  primaryButtonBackgroundColor,
  gray40,
  gray20,
  white,
  red50,
} from '../../constants/colors';
import { headerHeight, spHeaderHeight } from '../../constants/location';
import { userInviteMailSchema } from '../../validation/user';
import type { UserInviteMailType } from '../../validation/user/userType';

import CustomTextfield from '../uiParts/CustomTextfield';
import CustomSnackbar from '../uiParts/CustomSnackbar';
import { formLabelWrapper } from '../../constants/location';
import SideBar from '../uiParts/SideBar';
import { fetchSignInMethodsForEmail, sendSignInLinkToEmail } from 'firebase/auth';
import { createStaffUserWhenSendemail, userFindByEmail } from '../../api/firebase/firestore/user';
import { actionCodeSettings, auth } from '../../api/firebase/config';
import { throwError } from '../../modules/error';
import CustomLoading from '../uiParts/CustomLoading';
import { AuthContext } from '../../contexts/auth';
import CustomRadioButton from '../uiParts/CustomRadioButton';
import MetaTitle from '../utils/MetaTitle';
import { userRole } from '../../constants/enum';
import { getAllGroups } from '../../api/firebase/firestore/group';
import type { GroupType } from '../../validation/group/groupType';

const styles = {
  formLabelWrapper,
  button: {
    width: '400px',
    height: '44px',
    fontSize: '16px',
    fontWeight: '700',
    '@media screen and (max-width: 960px)': {
      width: '-webkit-fill-available',
    },
  },
  pageContainer: {
    backgroundColor: gray20,
    minHeight: `calc(100vh - ${headerHeight})`,
    display: 'flex',
    flexDirection: 'row',
    '@media screen and (max-width: 960px)': {
      flexDirection: 'column',
      minHeight: `calc(100vh - ${spHeaderHeight})`,
    },
  },
  inviteFormContainer: {
    alignItems: 'center',
    backgroundColor: white,
    borderRadius: '4px',
    border: `1px solid ${gray40}`,
    flexDirection: 'column',
    p: '39px',
    '@media screen and (max-width: 960px)': {
      width: '480px',
      padding: '32px 16px 34px 16px',
      boxSizing: 'border-box',
      margin: '0 16px',
      p: '32px 16px',
    },
    '@media screen and (max-width: 420px)': {
      width: '98%',
      padding: '32px 16px 34px 16px',
      boxSizing: 'border-box',
      margin: '0 16px',
      p: '32px 16px',
    },
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: `calc(100vh - ${headerHeight})`,
    '@media screen and (max-width:960px)': {
      width: '100%',
    },
    width: '560px',
    margin: '0 auto',
  },
  link: {
    color: gray100,
    display: 'block',
    fontWeight: '500',
    m: '24px 0px 0px 0px',
  },
  title: {
    '@media screen and (max-width:960px)': {
      fontSize: '20px',
    },
    color: gray100,
    fontSize: '22px',
    fontWeight: '500',
    mb: '24px',
    textAlign: 'left',
  },
  label: {
    fontSize: '14px',
  },
  radioLabel: {
    fontSize: '12px',
  },
  required: {
    color: red50,
    fontSize: '14px',
    marginLeft: '6px',
  },
};

const StaffInvite: React.FC = (): JSX.Element => {
  const [errorText, setErrorText] = useState('');
  const [open, setOpen] = useState(false);
  const [firstSubmit, setFirstSubmit] = useState(false);
  const { user, isLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [groups, setGroups] = useState<GroupType[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<UserInviteMailType>({
    resolver: yupResolver(userInviteMailSchema),
    defaultValues: {
      email: '',
      role: false,
    },
  });

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return watch('email') === undefined || '' || Boolean(errors.email);
    }
    return false;
  };

  // メール送信処理
  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      const email = watch('email');

      // メールアドレスが存在するか確認
      const isSignIn: string[] = await fetchSignInMethodsForEmail(auth, email);

      if (isSignIn.length) {
        setErrorText('このメールアドレスはすでに登録されています。');
        setOpen(true);
        return;
      }

      // firestoreのusersにメールが登録されているかチェック
      const user = await userFindByEmail(email);
      if (user) {
        setErrorText('このメールアドレスはすでに招待されています。');
        setOpen(true);
        return;
      }

      // 存在しなければfirestoreのusersに追加（選択したグループIDも保存）
      await createStaffUserWhenSendemail(email, watch, selectedGroupIds);

      // メール送信
      await sendSignInLinkToEmail(auth, email, actionCodeSettings(email));

      navigate('complete');
    } catch (e) {
      throwError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // ユーザーがスタッフ招待画面にアクセスした場合はスタッフ画面へ遷移
    if (user?.role !== userRole.admin) navigate('/staff');

    if (isLoading) return;
    // 未ログイン状態の場合はログイン画面へ遷移
    if (user === null) navigate('/');

    // グループ一覧を取得
    const fetchGroups = async (): Promise<void> => {
      try {
        const allGroups = await getAllGroups();
        setGroups(allGroups);
      } catch {
        setGroups([]);
      }
    };
    fetchGroups();
  }, [user, isLoading]);

  return (
    <>
      <MetaTitle />
      <CustomLoading loading={loading} />
      <Typography component="div" sx={styles.pageContainer}>
        <SideBar />
        <Typography component="div" sx={styles.container}>
          <Typography component="div" sx={styles.inviteFormContainer}>
            <Typography sx={styles.title}>担当者招待</Typography>
            <Controller
              render={({ field }): JSX.Element => (
                <CustomTextfield
                  {...field}
                  error={Boolean(errors.email)}
                  formMb="24px"
                  helperText={errors.email?.message}
                  labelText="メールアドレス"
                  placeholder="xxx@cto-hands.co.jp"
                  requiredText="必須"
                  width="400px"
                />
              )}
              control={control}
              name="email"
            />

            <Controller
              render={({ field }): React.ReactElement => (
                <CustomRadioButton
                  {...field}
                  error={Boolean(errors.role)}
                  helperText={errors.role?.message}
                  labelText="権限"
                  required
                  requiredText="必須"
                  trueOption="管理者"
                  falseOption="メンバー"
                  trueOptionDescription="担当者の閲覧・招待・編集ができます。"
                  falseOptionDescription="担当者の閲覧ができます。"
                />
              )}
              control={control}
              name="role"
            />

            {groups.length > 0 && (
              <Box sx={{ mb: '24px', width: '400px', textAlign: 'left' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '8px' }}>
                  <Typography sx={styles.label}>所属グループ</Typography>
                </Box>
                {groups.map(group => (
                  <FormControlLabel
                    key={group.groupId}
                    control={
                      <Checkbox
                        checked={selectedGroupIds.includes(group.groupId)}
                        onChange={(e): void => {
                          if (e.target.checked) {
                            setSelectedGroupIds(prev => [...prev, group.groupId]);
                          } else {
                            setSelectedGroupIds(prev => prev.filter(id => id !== group.groupId));
                          }
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: gray80,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '300px',
                        }}
                      >
                        {group.name}
                      </Typography>
                    }
                    sx={{ display: 'flex', mr: 0 }}
                  />
                ))}
              </Box>
            )}

            <Button
              disabled={buttonDisabledFlag()}
              onClick={async (): Promise<void> => {
                setFirstSubmit(true);
                await handleSubmit(async () => {
                  onSubmit();
                })();
              }}
              sx={[primaryButtonBackgroundColor, styles.button]}
              variant="contained"
            >
              招待メールを送る
            </Button>
          </Typography>
        </Typography>
        <CustomSnackbar
          autoHideDuration={6000}
          open={open}
          onClose={(): void => setOpen(false)}
          text={errorText}
        />
      </Typography>
    </>
  );
};

export default StaffInvite;
